import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Cog6ToothIcon,
  ChartBarIcon,
  UsersIcon,
  UserGroupIcon,
  EnvelopeIcon,
  ComputerDesktopIcon,
  WrenchScrewdriverIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';

const Settings = () => {
  const [systemInfo, setSystemInfo] = useState(null);

  useEffect(() => {
    apiService.makeRequest('/api/systemsettings/system-info')
      .then(data => setSystemInfo(data))
      .catch(() => setSystemInfo(null));
  }, []);

  const settingsItems = [
    {
      name: 'System Settings',
      description: 'Configure system-wide settings including timezone',
      href: '/system-settings',
      icon: WrenchScrewdriverIcon,
      color: 'bg-orange-500'
    },
    {
      name: 'Asset Management',
      description: 'Manage laptop assets and their assignments',
      href: '/assets',
      icon: ComputerDesktopIcon,
      color: 'bg-indigo-500'
    },
    {
      name: 'SLA Configuration',
      description: 'Configure Service Level Agreement rules and priorities',
      href: '/sla-configuration',
      icon: ChartBarIcon,
      color: 'bg-blue-500'
    },
    {
      name: 'User Management',
      description: 'Manage users, roles, and permissions',
      href: '/users',
      icon: UsersIcon,
      color: 'bg-green-500'
    },
    {
      name: 'Email Configuration',
      description: 'Manage category notification email templates',
      href: '/email-configuration',
      icon: EnvelopeIcon,
      color: 'bg-purple-500'
    },
    {
      name: 'Team Management',
      description: 'Create teams, assign technicians and route categories',
      href: '/teams',
      icon: UserGroupIcon,
      color: 'bg-teal-500'
    },
    {
      name: 'Category Management',
      description: 'Add, rename, or remove ticket categories',
      href: '/categories',
      icon: TagIcon,
      color: 'bg-pink-500'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Helpdesk Settings</h1>
        <p className="text-gray-600">Configure your helpdesk system settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {settingsItems.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            className="block group"
          >
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow h-32 flex items-center">
              <div className="flex items-center space-x-4 w-full">
                <div className={`${item.color} p-3 rounded-lg flex-shrink-0`}>
                  <item.icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                    {item.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {item.description}
                  </p>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className="bg-gray-500 p-3 rounded-lg">
            <Cog6ToothIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">System Information</h3>
            <p className="text-sm text-gray-600">Helpdesk system details and version</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Application:</span>
            <span className="ml-2 text-gray-600">{systemInfo?.application ?? 'Alula IT Helpdesk'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Version:</span>
            <span className="ml-2 text-gray-600">{systemInfo?.version ?? '—'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Environment:</span>
            <span className="ml-2 text-gray-600">{systemInfo?.environment ?? '—'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Database:</span>
            <span className="ml-2 text-gray-600">{systemInfo?.databaseType ?? '—'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Database Server:</span>
            <span className="ml-2 text-gray-600">{systemInfo?.databaseServer ?? '—'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Database Name:</span>
            <span className="ml-2 text-gray-600">{systemInfo?.databaseName ?? '—'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Connection:</span>
            <span className="ml-2 text-gray-600">{systemInfo?.connection ?? '—'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Backup:</span>
            <span className="ml-2 text-gray-600">{systemInfo?.backup ?? '—'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 